import type { ChangeEvent, FC, PropsWithChildren } from "react";
import { useEffect, useState } from "react";

import { useCheckoutConfiguration } from "@jti/commerce/providers";
import { Card } from "@jti/core-components/components/Card";
import { type Contact } from "@jti/fe-common";
import { FormField, useTranslations } from "@jti/ui";

import { OrderAttributes } from "@checkout/components/OrderAttributes";

import { useShippingFormContext } from "../../hooks/useShippingFormContext";

import { ShippingContactBottom } from "./ShippingContactBottom";

import styles from "./ShippingContact.module.scss";

type ShippingContactProps = {
  initialContactData?: Contact;
  onEdit?(countryCode: string, number: string, phoneVerificationUuid: string): void;
  isPhoneValidationErrorVisible?: boolean;
  isAccountPhoneNumberInvalid?: boolean;
};

export const ShippingContact: FC<PropsWithChildren<ShippingContactProps>> = ({
  children,
  initialContactData,
  onEdit,
  isPhoneValidationErrorVisible,
  isAccountPhoneNumberInvalid,
}) => {
  const { __ } = useTranslations();
  const { contactConfig } = useCheckoutConfiguration();
  const {
    watch,
    clearErrors,
    setValue,
    formState: { errors: formErrors },
  } = useShippingFormContext();

  const code = watch("editPhone.code");
  const telephone: string = watch("editPhone.phone");
  const shouldUseAccountContact = watch("shouldUseAccountContact");

  const isAllowedUsingAccountData = !(contactConfig?.differentRecipientDisabled || isAccountPhoneNumberInvalid);

  const [sameRecipient, setSameRecipient] = useState(shouldUseAccountContact);
  const [phoneNumber, setPhoneNumber] = useState<{ code: string; telephone: string }>({
    code: code?.value,
    telephone,
  });

  const handleCheckboxChange = (event: ChangeEvent<HTMLInputElement>) => {
    const useAccount = event.target.checked;

    const phone = useAccount ? (initialContactData?.telephone ?? "") : telephone;

    setValue("shouldUseAccountContact", useAccount);
    setSameRecipient(useAccount);
    setValue("shippingContact.telephone", phone);
  };

  useEffect(() => {
    if (shouldUseAccountContact) {
      clearErrors(["shippingContact", "editPhone"]);
    }
  }, [clearErrors, shouldUseAccountContact, initialContactData]);

  useEffect(() => {
    if (onEdit && (telephone !== phoneNumber?.telephone || code?.value !== phoneNumber.code)) {
      setPhoneNumber({ code: code?.value, telephone: telephone });
      onEdit(code?.value, telephone, "");

      if (formErrors.root?.shippingAddress) {
        clearErrors("root.shippingAddress");
      }
    }
  }, [code, telephone]);

  useEffect(() => {
    if (!initialContactData) {
      return;
    }

    setValue(`shippingContact.firstname`, initialContactData.firstname);
    setValue(`shippingContact.lastname`, initialContactData.lastname);
  }, [initialContactData?.firstname, initialContactData?.lastname, setValue]);

  return (
    <Card title={__("cart.checkout.shipping_contact.title")} data-testid="card-container" variant="level-1">
      <div className={styles.intro}>{__("cart.checkout.shipping_contact.intro")}</div>
      {isAllowedUsingAccountData && (
        <FormField
          id="set-contact-details-editing"
          className={styles.useAccountContact}
          name="useAccountContact"
          dataTestId="set-contact-details-editing"
          render={() => (
            <label className={styles.checkboxLabel} htmlFor="set-contact-details-editing-input">
              <input
                className={styles.checkbox}
                id="set-contact-details-editing-input"
                type="checkbox"
                checked={sameRecipient}
                onChange={handleCheckboxChange}
              />
              {__("cart.checkout.shipping_contact.use_account")}
            </label>
          )}
        />
      )}
      <OrderAttributes target={"contactDetails_before"} />
      <ShippingContactBottom
        sameRecipient={sameRecipient}
        contact={initialContactData}
        isPhoneValidationErrorVisible={isPhoneValidationErrorVisible}
        isAccountPhoneNumberInvalid={isAccountPhoneNumberInvalid}
      />
      <OrderAttributes target={"contactDetails_after"} />
      {children}
    </Card>
  );
};
